import { Link } from '@remix-run/react';
import { DotIcon, ExternalLinkIcon } from 'lucide-react';
import { PageSubHeading } from '~/components/PageSubHeading';
import { TagLink } from '~/components/TagLink';
import { AffiliateLinks } from '~/routes/pedals.$pedalId/AffiliateLinks';

interface Props {
  pedal: Pedal;
  imageUrlPrefix: string;
  amazonAssociateId?: string;
  rakutenAffiliateUrl?: string;
}

interface Pedal {
  id: string;
  name: string;
  description: string;
  specification: string;
  brand: {
    id: string;
    name: string;
  };
  images: {
    url: string;
  }[];
  externalLinks: {
    id: string;
    title: string;
    url: string;
  }[];
  tags: {
    tagId: string;
    tag: {
      tag: string;
    };
  }[];
}

export const PedalDetail = ({ pedal, imageUrlPrefix, amazonAssociateId, rakutenAffiliateUrl }: Props) => {
  return (
    <section className="text-gray-600 body-font overflow-hidden bg-white">
      <div className="">
        <div className="mx-auto flex flex-wrap bg-white">

          {/* モバイルで表示するペダル名 */}
          <div className={'block md:hidden'}>
            <PedalHeader
              brandId={pedal.brand.id}
              brandName={pedal.brand.name}
              pedalName={pedal.name}
              tags={pedal.tags}
            />
          </div>

          <div className={'md:w-1/3 w-full'}>
            <img
              alt={`${pedal.name} - ${pedal.brand.name}`}
              className=" rounded-2xl border border-slate-200 bg-white w-full"
              src={ pedal.images && pedal.images.length > 0
                ? `${imageUrlPrefix}${pedal.images[0].url}`
                : '/no-image-s.png'
              }
            />
          </div>
          <div className="md:w-2/3 w-full md:pl-10 md:py-6 md:mt-0 mt-6 ">
            {/* デスクトップで表示するペダル名 */}
            <div className={'hidden md:block'}>
              <PedalHeader
                brandId={pedal.brand.id}
                brandName={pedal.brand.name}
                pedalName={pedal.name}
                tags={pedal.tags}
              />
            </div>

            <div className="break-words whitespace-pre-wrap">
              <p className="leading-relaxed text-[14px] mt-4">{pedal.description}</p>
            </div>
            {/* <div className="flex mt-1 items-center pb-5 border-b-2 border-gray-100 mb-5">
            </div> */}

            {pedal.specification && (
              <>
                <div className="mt-6">
                  <PageSubHeading>
                    仕様
                  </PageSubHeading>
                </div>

                <div className={'text-[14px] break-words whitespace-pre-wrap'}>
                  {pedal.specification}
                </div>
              </>
            )}

            {pedal.externalLinks.length > 0 && (
              <>
                <div className="mt-6">
                  <PageSubHeading>
                    関連URL
                  </PageSubHeading>
                </div>
                <div className={'text-[14px]'}>
                  {pedal.externalLinks.map((externalLink) => (
                    <div key={externalLink.id} className={'flex items-start mb-1'}>
                      <DotIcon className={'w-4 h-4 mr-1 mt-1'} />
                      <Link
                        to={externalLink.url}
                        target={'_blank'}
                        className={'flex items-center text-indigo-500 hover:underline hover:text-indigo-700'}
                        rel="noreferrer"
                      >
                        <span className="flex items-center">
                          {externalLink.title}
                          <ExternalLinkIcon className={'w-4 h-4 ml-1 flex-shrink-0'} />
                        </span>
                      </Link>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className='mt-10'>
              <AffiliateLinks pedal={pedal} amazonAssociateId={amazonAssociateId} rakutenAffiliateUrl={rakutenAffiliateUrl} />
            </div>

          </div>
        </div>

      </div>
    </section>
  );
};

interface PedalHeaderProps {
  brandId: string;
  brandName: string;
  pedalName: string;
  tags: {
    tagId: string;
    tag: {
      tag: string;
    };
  }[];
}
const PedalHeader = ({ brandId, brandName, pedalName, tags }: PedalHeaderProps) => {
  return (
    <>
      <h2 className="text-sm title-font text-gray-500 tracking-widest">
        <Link to={`/brands/${brandId}`} className="hover:text-indigo-400">
          {brandName}
        </Link>
      </h2>
      <h1 className="text-gray-900 text-3xl title-font font-semibold mt-1 mb-1">
        {pedalName}
      </h1>
      <div className="flex mt-4 mb-6 gap-2">
        {tags.map((tag) => (
          <TagLink key={tag.tagId} id={tag.tagId} tag={tag.tag.tag} />
        ))}
      </div>
    </>
  )
}